import React from "react";
import _ from "lodash";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import createStyles from "@material-ui/core/styles/createStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import TreeView from "@material-ui/lab/TreeView";
import TreeItem from "@material-ui/lab/TreeItem";

import { PageHeader, PageRoot, Button } from "../components";
import routes from "../constants/routes";
import { connect } from "react-redux";
import { Resource } from "../constants/interfaces";
import { resourceActions } from "../reducers/resources";
import { Icon } from "../components/icons/Icon";
import SearchBar from "../components/SearchBar";
import downloadFile from "../utils/downloadFile";

const styles = (theme: Theme) =>
  createStyles({
    root: {},
    treeItemRoot: {
      padding: "0.5em",
      boxShadow: theme.shadows[1],
      marginTop: "0.8em",
      color: theme.palette.text.secondary
    },
    content: {
      padding: "0.5em",
      marginTop: "2px"
    },
    cateLabel: {
      fontSize: "1.2rem"
    },
    subCateLabel: {
      color: theme.palette.primary.main
    },
    resource: {
      display: "flex",
      alignItems: "center"
    },
    search: {
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center"
    },
    itemElement: {
      margin: "0 1em",
      color: theme.palette.primary.main
    },
    infoIcon: {
      cursor: "pointer",
      fontSize: 20
    },
    modalPaper: {
      padding: theme.spacing(4)
    },
    modalTitleWrapper: {
      alignItems: "center",
      display: "flex",
      marginBottom: theme.spacing(2)
    },
    modalIcon: {
      marginRight: theme.spacing(1)
    },
    modalButtonWrapper: {
      display: "flex",
      justifyContent: "flex-end",
      marginTop: theme.spacing(2)
    }
  });

type TreeNode = {
  label: string;
  nodeId: string;
  children?: any;
};

type Props = {
  classes?: any;
  resources: Resource[];
  getResources: () => Promise<Resource[]>;
};

type State = {
  searchInput: string;
  activeResource: Resource | null;
  doShowResourceInfo: boolean | false;
};

class Resources extends React.Component<Props, State> {
  componentDidMount() {
    this.props.getResources();
  }

  state = {
    searchInput: "",
    activeResource: null,
    doShowResourceInfo: false
  };

  getResourceTree(searchInput: string) {
    const resources = !searchInput
      ? this.props.resources
      : this.props.resources.filter(resource =>
          resource.resourceName
            .toLocaleLowerCase()
            .includes(searchInput.toLocaleLowerCase())
        );

    const expand = _.uniq(
      _.flatten(
        resources.map(r => [r.resourceNameCategory, r.resourceNameSubCategory])
      )
    );

    const resourceTree = resources.reduce(
      (tree, resource) => {
        let categoryNode = tree.find(
          t => t.nodeId === resource.resourceNameCategory
        );

        if (!categoryNode) {
          categoryNode = {
            nodeId: resource.resourceNameCategory,
            label: resource.resourceNameCategory,
            children: []
          };
          tree.push(categoryNode);
        }

        let subCateNode = categoryNode.children.find(
          (subCate: TreeNode) =>
            subCate.nodeId === resource.resourceNameSubCategory
        );

        if (!subCateNode) {
          subCateNode = {
            nodeId: resource.resourceNameSubCategory,
            label: resource.resourceNameSubCategory,
            children: []
          };
          categoryNode.children.push(subCateNode);
        }

        subCateNode.children.push({
          ...resource,
          nodeId: resource.cmAcmCaseIdentifier,
          label: resource.resourceName
        });

        return tree;
      },
      [] as TreeNode[]
    );
    return { resourceTree, expand };
  }

  render() {
    const { classes } = this.props;

    const { resourceTree, expand } = this.getResourceTree(
      this.state.searchInput
    );

    return (
      <PageRoot>
        <PageHeader
          breadcrumbLinks={[
            {
              label: routes.HOME.label,
              route: routes.HOME.url
            }
          ]}
          label={routes.RESOURCES.label}
        />

        <div className={classes.search}>
          <SearchBar
            disabled={search => search.length < 3}
            onReset={() => {
              this.setState({ searchInput: "" });
            }}
            onSearch={searchInput => {
              this.setState({ searchInput });
            }}
          />
        </div>

        <TreeView
          key={this.state.searchInput} // to destroy TreeView
          className={classes.root}
          defaultExpanded={this.state.searchInput ? expand : []}
          defaultCollapseIcon={<Icon name="arrow_drop_down" />}
          defaultExpandIcon={<Icon name="arrow_right" />}
          defaultEndIcon={<div style={{ width: 24 }} />}
        >
          {resourceTree.map(cateNode => (
            <TreeItem
              classes={{
                label: classes.cateLabel,
                root: classes.treeItemRoot,
                content: classes.content
              }}
              key={cateNode.nodeId}
              nodeId={cateNode.nodeId}
              label={cateNode.label}
            >
              {cateNode.children.map((subCate: TreeNode) => {
                return (
                  <TreeItem
                    classes={{
                      content: classes.content
                    }}
                    key={subCate.nodeId}
                    nodeId={subCate.nodeId}
                    label={subCate.label}
                  >
                    {subCate.children.map((resource: any) => (
                      <div
                        className={classes.resource}
                        key={resource.cmAcmCaseIdentifier}
                      >
                        {resource.resourceDocType === "External URL" ? (
                          <a
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              height: 30,
                              width: 30
                            }}
                            href={`${resource.resourceURL}`}
                          >
                            <Icon color="primary" name="open_in_new" />
                          </a>
                        ) : (
                          <IconButton
                            onClick={() => {
                              const token = window.localStorage.getItem(
                                "token"
                              );
                              const url = `/mcols/api/resource/downloadFile?caseId=${resource.cmAcmCaseIdentifier}&token=${token}`;
                              downloadFile({ url });
                            }}
                            size="small"
                          >
                            <Icon color="primary" name="get_app" />
                          </IconButton>
                        )}

                        <Typography className={classes.itemElement}>
                          {resource.resourceName}
                        </Typography>

                        <Icon
                          onClick={() => {
                            this.setState({ doShowResourceInfo: true, activeResource: resource });
                          }}
                          color="primary"
                          className={classes.infoIcon}
                          name="info_outline"
                        />
                      </div>
                    ))}
                  </TreeItem>
                );
              })}
            </TreeItem>
          ))}
        </TreeView>

        <Dialog
          open={this.state.doShowResourceInfo}
          onClose={() => this.setState({ doShowResourceInfo: false, activeResource: null })}
          classes={{
            paper: classes.modalPaper
          }}
        >
          <div className={classes.modalTitleWrapper}>
            <Icon
              className={classes.modalIcon}
              color="primary"
              name="info_outline"
            />
            <Typography id="modal-title" color="primary" variant="h5">
              {_.get(this.state.activeResource, "resourceName")}
            </Typography>
          </div>
          <Typography>
            {_.get(this.state.activeResource, "resourceDescription")}
          </Typography>
          <div className={classes.modalButtonWrapper}>
            <Button
              id="done"
              onClick={() => this.setState({ doShowResourceInfo: false })}
            >
              Done
            </Button>
          </div>
        </Dialog>
      </PageRoot>
    );
  }
}
const mapStateToProps = ({ resources }: any) => {
  return {
    error: resources.error,
    loading: resources.loading,
    resources: resources.resources
  };
};
const mapDispatchToProps = {
  getResources: resourceActions.getResources
};

export const styled = withStyles(styles)(Resources);
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled);
